import { buildComposite, compositeFromSpec, type CompositeState } from "./compositeReportModel.ts";
// CRB1 (docs/build-briefs/CRB1-custom-report-builder-core.md; design/mockups/report-builder/03a and 03c): the pure model of
// the custom report builder screen. No DOM and nothing node-only, so a plain node:test file imports it
// (test/crb1-ui-model.test.ts) and the browser bundle does too.
//
// What it does: holds the builder's choices, turns them into a version 1 report spec (the only thing the server accepts:
// catalog field names and values, never a column or an expression), checks them the way the server will so the person is
// told before Run, and formats a result for the table. The server re-validates everything; nothing here decides what a
// person may see.

import type {
  CustomReportCatalogDto,
  CustomReportCell,
  CustomReportColumnDto,
  CustomReportFieldDto,
  CustomReportResultDto,
} from "./api.ts";
import { chartOf, type ChartSpecDto, type ChartType } from "./reportChartModel.ts";
import { formatCount, formatMoney } from "./reportFormat.ts";

export type CatalogDataset = CustomReportCatalogDto["catalog"]["datasets"][number];

/** Relative dates are resolved at run time; custom reports support a complete calendar year. */
export const CR_PRESETS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "this_week", label: "This week" },
  { id: "last_week", label: "Last week" },
  { id: "last_7_days", label: "Last 7 days" },
  { id: "last_30_days", label: "Last 30 days" },
  { id: "last_12_weeks", label: "Last 12 weeks" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "quarter_to_date", label: "Quarter to date" },
  { id: "last_quarter", label: "Last quarter" },
  { id: "year_to_date", label: "Year to date" },
  { id: "last_year", label: "Last year" },
  { id: "last_12_months", label: "Last 12 months" },
];
export const CR_CUSTOM_DATES = "custom";
export const CR_DEFAULT_PRESET = "last_30_days";

/** The row limits offered; null is all rows up to the server's cap. */
export const CR_ROW_LIMITS: ReadonlyArray<{ value: number | null; label: string }> = [
  { value: null, label: "All" },
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
  { value: 500, label: "500" },
];

export const CR_AGG_LABELS: Readonly<Record<string, string>> = {
  sum: "Sum",
  count: "Count",
  min: "Min",
  max: "Max",
  avg: "Average",
  ratio: "Ratio",
  share: "Share",
};

/** CRB3: the compare choices, in the order of mockup 02b: Off, Prior, Last year. */
export type CompareChoice = "" | "prior_period" | "same_period_last_year";
export const CR_COMPARE_CHOICES: ReadonlyArray<{ value: CompareChoice; label: string }> = [
  { value: "", label: "Off" },
  { value: "prior_period", label: "Prior period" },
  { value: "same_period_last_year", label: "Last year" },
];

/** CRB3: the Show as row of mockup 03a. Table is no chart. */
export const CR_SHOW_AS: ReadonlyArray<{ type: ChartType | null; label: string }> = [
  { type: null, label: "Table" },
  { type: "bar", label: "Bar" },
  { type: "line", label: "Line" },
  { type: "stacked_bar", label: "Stacked" },
  { type: "pie", label: "Pie" },
  { type: "number", label: "Number" },
];

export interface MeasureChoice {
  field: string;
  agg: string;
}

export interface FilterDraft {
  /** A key for the row, so a re-render keeps the person's place. */
  id: number;
  field: string;
  op: string;
  /** What the person typed or picked. Money is dollars; a date is YYYY-MM-DD; a boolean is "true" or "false". */
  value: string;
  /** The second value of a between. */
  value2: string;
}

export interface BuilderState {
  composite?: CompositeState;
  /** Omitted keeps the existing totals-on default. */
  totals?: boolean;
  tile?: {label:string};
  title: string;
  dataset: string;
  /** "all" (every location the person may report on) or one location id. */
  location: string;
  /** A preset id from CR_PRESETS, or CR_CUSTOM_DATES with `from` and `to`. */
  dates: string;
  from: string;
  to: string;
  dimensions: string[];
  measures: MeasureChoice[];
  filters: FilterDraft[];
  /** A column key (a dimension name or a measure's alias) and a direction; null keeps the default order. */
  sortBy: string;
  sortDir: "asc" | "desc";
  limit: number | null;
  nextFilterId: number;
  /** CRB3: the chart drawn above the table, or null for the table alone. */
  chart: ChartType | null;
  /** CRB3: compare with the prior period or last year; "" is off. */
  compare: CompareChoice;
}

export function freshBuilder(dataset: string): BuilderState {
  return {
    title: "",
    dataset,
    location: "all",
    dates: CR_DEFAULT_PRESET,
    from: "",
    to: "",
    dimensions: [],
    measures: [],
    filters: [],
    sortBy: "",
    sortDir: "desc",
    limit: null,
    nextFilterId: 1,
    chart: null,
    compare: "",
  };
}

export function fieldNamed(dataset: CatalogDataset, name: string): CustomReportFieldDto | undefined {
  return dataset.fields.find((f) => f.name === name);
}

export function dimensionFields(dataset: CatalogDataset): CustomReportFieldDto[] {
  return dataset.fields.filter((f) => f.kind === "dimension");
}

export function measureFields(dataset: CatalogDataset): CustomReportFieldDto[] {
  return dataset.fields.filter((f) => f.kind === "measure");
}

/** The fields the "+ Filter" picker offers: every field but a share, which is a column of the report and never a test (a share
 * is a window over the groups, which SQL does not allow in HAVING). */
export function filterableFields(dataset: CatalogDataset): CustomReportFieldDto[] {
  return dataset.fields.filter((f) => !(f.kind === "measure" && f.aggregations.every((agg) => agg === "share")));
}

/** The fields whose label contains the query, case blind. An empty query keeps every field. */
export function searchFields(fields: readonly CustomReportFieldDto[], query: string): CustomReportFieldDto[] {
  const needle = query.trim().toLowerCase();
  return needle === "" ? [...fields] : fields.filter((f) => f.label.toLowerCase().includes(needle));
}

/** The first aggregation a measure offers when it is added: a sum where there is one, else its only one. */
export function defaultAgg(field: CustomReportFieldDto): string {
  return field.aggregations.includes("sum") ? "sum" : (field.aggregations[0] ?? "sum");
}

/** The key of a measure's result column. The same short name goes in the spec as `as`, so a sort names it. */
export function measureAlias(measure: MeasureChoice): string {
  return `${measure.field}_${measure.agg}`.slice(0, 32);
}

export function addDimension(state: BuilderState, name: string): BuilderState {
  if (state.dimensions.includes(name) || state.dimensions.length >= 4) return state;
  return { ...state, dimensions: [...state.dimensions, name] };
}

export function removeDimension(state: BuilderState, name: string): BuilderState {
  return {
    ...state,
    dimensions: state.dimensions.filter((d) => d !== name),
    sortBy: state.sortBy === name ? "" : state.sortBy,
  };
}

export function addMeasure(state: BuilderState, field: CustomReportFieldDto): BuilderState {
  if (state.measures.length >= 8) return state;
  const measure = { field: field.name, agg: defaultAgg(field) };
  if (state.measures.some((m) => measureAlias(m) === measureAlias(measure))) return state;
  return { ...state, measures: [...state.measures, measure] };
}

export function setMeasureAgg(state: BuilderState, index: number, agg: string): BuilderState {
  const current = state.measures[index];
  if (current === undefined) return state;
  const next = { ...current, agg };
  if (state.measures.some((m, at) => at !== index && measureAlias(m) === measureAlias(next))) return state;
  const sortBy = state.sortBy === measureAlias(current) ? measureAlias(next) : state.sortBy;
  return { ...state, measures: state.measures.map((m, at) => (at === index ? next : m)), sortBy };
}

export function removeMeasure(state: BuilderState, index: number): BuilderState {
  const gone = state.measures[index];
  if (gone === undefined) return state;
  return {
    ...state,
    measures: state.measures.filter((_, at) => at !== index),
    sortBy: state.sortBy === measureAlias(gone) ? "" : state.sortBy,
  };
}

/** The comparison choices for a filter on a field, as the server accepts them for its type. */
export function filterOpsFor(field: CustomReportFieldDto): Array<{ op: string; label: string }> {
  switch (field.type) {
    case "text":
      return [
        { op: "eq", label: "is" },
        { op: "neq", label: "is not" },
        { op: "contains", label: "contains" },
      ];
    case "enum":
      return [
        { op: "eq", label: "is" },
        { op: "neq", label: "is not" },
      ];
    case "boolean":
      return [{ op: "is", label: "is" }];
    case "date":
      return [{ op: "between", label: "is between" }];
    default:
      return [
        { op: "gte", label: "is at least" },
        { op: "lte", label: "is at most" },
        { op: "gt", label: "is more than" },
        { op: "lt", label: "is less than" },
        { op: "eq", label: "is exactly" },
        { op: "between", label: "is between" },
      ];
  }
}

export function addFilter(state: BuilderState, field: CustomReportFieldDto): BuilderState {
  if (state.filters.length >= 10) return state;
  const op = filterOpsFor(field)[0]?.op ?? "eq";
  const value = field.type === "boolean" ? "true" : field.type === "enum" ? (field.values?.[0] ?? "") : "";
  return {
    ...state,
    filters: [...state.filters, { id: state.nextFilterId, field: field.name, op, value, value2: "" }],
    nextFilterId: state.nextFilterId + 1,
  };
}

export function removeFilter(state: BuilderState, id: number): BuilderState {
  return { ...state, filters: state.filters.filter((f) => f.id !== id) };
}

export function updateFilter(state: BuilderState, id: number, change: Partial<Omit<FilterDraft, "id" | "field">>): BuilderState {
  return { ...state, filters: state.filters.map((f) => (f.id === id ? { ...f, ...change } : f)) };
}

const MONEY_TEXT = /^\d{1,12}(\.\d{1,2})?$/;
const DECIMAL_TEXT = /^-?\d{1,12}(\.\d{1,6})?$/;
const DATE_TEXT = /^\d{4}-\d{2}-\d{2}$/;

/** Dollars typed as "12.50" or "12" as integer cents; null when it is not money. */
export function dollarsToCents(text: string): number | null {
  const trimmed = text.trim().replace(/^\$/, "").replace(/,/g, "");
  if (!MONEY_TEXT.test(trimmed)) return null;
  const [whole, fraction = ""] = trimmed.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

function realDate(text: string): boolean {
  if (!DATE_TEXT.test(text)) return false;
  const [y, m, d] = text.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12)).toISOString().slice(0, 10) === text;
}

function filterValue(field: CustomReportFieldDto, text: string): string | number | boolean | undefined {
  switch (field.type) {
    case "money": {
      const cents = dollarsToCents(text);
      return cents === null ? undefined : cents;
    }
    case "number": {
      return /^\d{1,12}$/.test(text.trim()) ? Number(text.trim()) : undefined;
    }
    case "decimal":
    case "percent": {
      return DECIMAL_TEXT.test(text.trim()) ? Number(text.trim()) : undefined;
    }
    case "boolean":
      return text === "true" ? true : text === "false" ? false : undefined;
    case "date":
      return realDate(text) ? text : undefined;
    case "enum":
      return field.values?.includes(text) === true ? text : undefined;
    default:
      return text.trim() === "" ? undefined : text.trim();
  }
}

export type SpecResult = { ok: true; spec: Record<string, unknown> } | { ok: false; message: string };

/** The version 1 report spec for the builder's choices, or one sentence saying what is missing. */
export function buildSpec(state: BuilderState, dataset: CatalogDataset, datasets: CatalogDataset[] = [dataset]): SpecResult {
  if (state.composite !== undefined) return buildComposite(state, datasets);
  if (state.dimensions.length === 0 && state.measures.length === 0) {
    return { ok: false, message: "Pick a measure or a field to group by." };
  }
  const filters: Array<Record<string, unknown>> = [];
  for (const draft of state.filters) {
    const field = fieldNamed(dataset, draft.field);
    if (field === undefined) return { ok: false, message: "A filter names a field that is not available." };
    const first = filterValue(field, draft.value);
    if (first === undefined) return { ok: false, message: `Check the value for ${field.label}.` };
    const filter: Record<string, unknown> = { field: draft.field, op: draft.op };
    if (field.kind === "measure" && !field.aggregations.includes(field.ratio === undefined ? "sum" : "ratio")) {
      // The server totals a measure filter with a sum (a ratio with its ratio) unless told otherwise. A figure that is never
      // summed (a quantity beside its unit, a unit cost) is filtered the way the report totals it, else the way it is added.
      filter["agg"] = state.measures.find((m) => m.field === field.name)?.agg ?? defaultAgg(field);
    }
    if (draft.op === "between") {
      const second = filterValue(field, draft.value2);
      if (second === undefined) return { ok: false, message: `Check the second value for ${field.label}.` };
      if (first > second) return { ok: false, message: `${field.label} must run from low to high.` };
      filter["values"] = [first, second];
    } else {
      filter["value"] = first;
    }
    filters.push(filter);
  }
  let dateRange: Record<string, unknown>;
  if (state.dates === CR_CUSTOM_DATES) {
    if (!realDate(state.from) || !realDate(state.to)) return { ok: false, message: "Pick a start and an end date." };
    if (state.from > state.to) return { ok: false, message: "The start date is after the end date." };
    dateRange = { kind: "absolute", from: state.from, to: state.to };
  } else {
    dateRange = { kind: "relative", preset: state.dates };
  }
  const spec: Record<string, unknown> = {
    specVersion: 1,
    dataset: dataset.id,
    dimensions: state.dimensions,
    measures: state.measures.map((m) => ({ field: m.field, agg: m.agg, as: measureAlias(m) })),
    filters,
    dateRange,
    locations: state.location === "all" ? { kind: "all_permitted" } : { kind: "list", ids: [state.location] },
    sort: state.sortBy === "" ? [] : [{ by: state.sortBy, dir: state.sortDir }],
    output: { view: "table", totals: state.totals !== false },
  };
  if (state.compare !== "") spec["compare"] = { kind: state.compare };
  if (state.chart !== null) {
    const slot = chartSlot(state, dataset);
    if (!slot.ok) return { ok: false, message: slot.message };
    spec["output"] = { view: "table", totals: state.totals !== false, chart: slot.chart };
  }
  if (state.tile !== undefined) {
    const measure = state.measures[0];
    if (state.dimensions.length !== 0 || state.measures.length !== 1 || measure === undefined) return {ok:false,message:"A tile needs one measure and no grouping."};
    const label = state.tile.label.trim();
    if (label === "" || label.length > 120) return {ok:false,message:"Name the tile."};
    (spec["output"] as Record<string,unknown>)["tile"] = {measure:measureAlias(measure),label};
  }
  if (state.title.trim() !== "") spec["title"] = state.title.trim().slice(0, 120);
  if (state.limit !== null) spec["limit"] = state.limit;
  return { ok: true, spec };
}

/** What kind of figure a measure column shows, as the server types it (src/domain/reportBuilder/compile.ts measureResultType). */
function measureUnit(field: CustomReportFieldDto, agg: string): string {
  if (agg === "share") return "percent";
  if (agg === "count") return "number";
  if (field.type === "decimal" || field.type === "percent") return "decimal";
  if (agg === "avg") return field.type === "money" ? "money" : "decimal";
  return field.type === "money" ? "money" : "number";
}

const CHART_RULE_MESSAGES: Readonly<Record<ChartType, string>> = {
  bar: "A bar chart needs one or two groups and a measure.",
  line: "A line chart needs one or two groups and a measure.",
  stacked_bar: "A stacked chart needs one or two groups and a measure.",
  pie: "A pie chart needs one group and one measure.",
  number: "A number needs one measure and no groups.",
};

/**
 * The chart slot the spec will carry for the builder's choices, from the groups and measures: the first group is across, the
 * second is the series, the first measure (and any other of its kind) is up. The same rules as the server's validator
 * (src/domain/reportBuilder/spec.ts), so a chart that cannot be drawn is said before Run.
 */
export function chartSlot(
  state: BuilderState,
  dataset: CatalogDataset,
  type: ChartType | null = state.chart,
): { ok: true; chart: ChartSpecDto } | { ok: false; message: string } {
  if (type === null) return { ok: false, message: "Pick a chart." };
  const refused = { ok: false as const, message: CHART_RULE_MESSAGES[type] };
  const aliases = state.measures.map(measureAlias);
  const units = state.measures.map((m) => {
    const field = fieldNamed(dataset, m.field);
    return field === undefined ? "" : measureUnit(field, m.agg);
  });
  const dims = state.dimensions;
  if (aliases.length === 0) return refused;
  if (type === "number") {
    return dims.length === 0 && aliases.length === 1
      ? { ok: true, chart: { type, x: null, y: [aliases[0] as string], series: null } }
      : refused;
  }
  if (type === "pie") {
    return dims.length === 1 && aliases.length === 1
      ? { ok: true, chart: { type, x: dims[0] as string, y: [aliases[0] as string], series: null } }
      : refused;
  }
  if (dims.length < 1 || dims.length > 2) return refused;
  const series = dims[1] ?? null;
  const y = series === null ? aliases.filter((_, at) => units[at] === units[0]) : [aliases[0] as string];
  return { ok: true, chart: { type, x: dims[0] as string, y, series } };
}

/** Whether Show as offers a chart type for the builder's choices: Table always, a chart when its rules hold. */
export function chartAvailable(type: ChartType | null, state: BuilderState, dataset: CatalogDataset): boolean {
  return type === null || chartSlot(state, dataset, type).ok;
}

/** The columns the report will have, for the Sort choice: grouped fields then measures, as the table shows them. */
export function sortChoices(state: BuilderState, dataset: CatalogDataset): Array<{ by: string; label: string }> {
  const choices: Array<{ by: string; label: string }> = [];
  for (const name of state.dimensions) {
    const field = fieldNamed(dataset, name);
    if (field !== undefined) choices.push({ by: name, label: field.label });
  }
  for (const measure of state.measures) {
    const field = fieldNamed(dataset, measure.field);
    if (field !== undefined) {
      choices.push({
        by: measureAlias(measure),
        label: measure.agg === "sum" || measure.agg === "ratio" ? field.label : `${field.label} (${CR_AGG_LABELS[measure.agg] ?? measure.agg})`,
      });
    }
  }
  return choices;
}

// ---------------------------------------------------------------------
// The result
// ---------------------------------------------------------------------

export const NOT_AVAILABLE = "-";

/** A cell as the table shows it: money with a dollar sign, a count with separators, Yes or No, N/A for empty. */
export function formatCell(
  value: CustomReportCell,
  type: CustomReportColumnDto["type"],
  valueLabels?: Readonly<Record<string, string>>,
): string {
  if (value === null || value === undefined) return NOT_AVAILABLE;
  switch (type) {
    case "percent":
      return typeof value === "number" ? `${(value / 100).toFixed(1)}%` : NOT_AVAILABLE;
    case "money":
      return typeof value === "number" ? formatMoney(value) : NOT_AVAILABLE;
    case "number":
      return typeof value === "number" ? formatCount(value) : NOT_AVAILABLE;
    case "decimal": {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed.toLocaleString("en-US", { maximumFractionDigits: 2 }) : NOT_AVAILABLE;
    }
    case "boolean":
      return value === true ? "Yes" : "No";
    default:
      return typeof value === "string" ? (valueLabels?.[value] ?? value) : String(value);
  }
}

/** The words of each enum field of a dataset, by field name (the catalog's), for the table and the chart. */
export function valueLabelsOf(dataset: CatalogDataset | undefined): Record<string, Record<string, string>> {
  const labels: Record<string, Record<string, string>> = {};
  for (const field of dataset?.fields ?? []) {
    if (field.valueLabels !== undefined) labels[field.name] = field.valueLabels;
  }
  return labels;
}

/** Columns that hold a figure sit to the right. */
export function isFigure(column: CustomReportColumnDto): boolean {
  return column.type === "money" || column.type === "number" || column.type === "decimal" || column.type === "percent";
}

/** The totals row cells aligned to the table's columns: a label under the first column, the measure totals under the
 * measure columns, and nothing under a grouped column. Null when there is no totals row. */
export function totalsRow(result: CustomReportResultDto): string[] | null {
  if (result.totals === null) return null;
  // A report with nothing grouped is one row, which is its own total: a second row would only repeat it.
  if (!result.columns.some((column) => column.kind === "dimension")) return null;
  const cells: string[] = [];
  let measureAt = 0;
  for (const [index, column] of result.columns.entries()) {
    if (column.kind === "measure") {
      cells.push(formatCell(result.totals[measureAt] ?? null, column.type));
      measureAt += 1;
    } else {
      cells.push(index === 0 ? "Total" : "");
    }
  }
  return cells;
}

export function rowCountText(result: CustomReportResultDto): string {
  const n = result.meta.rowCount;
  return `${formatCount(n)} ${n === 1 ? "row" : "rows"}`;
}

/** The sentence under the table when the rows were cut at the cap. Empty when they were not. */
export function truncationText(result: CustomReportResultDto, exportCap: number): string {
  return result.meta.truncated
    ? `Showing the first ${formatCount(result.meta.rowCap)} rows. Export for up to ${formatCount(exportCap)}.`
    : "";
}

/** "Mar 2, 2026" from a business date. */
function longDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function dateSpanText(from: string, to: string): string {
  return from === to ? longDate(from) : `${longDate(from)} to ${longDate(to)}`;
}

/** "All 3 locations" or the one name; the words follow the design ("locations" throughout). */
export function locationsText(locations: ReadonlyArray<{ name: string }>, permitted: number): string {
  if (locations.length === 1) return locations[0]?.name ?? NOT_AVAILABLE;
  return locations.length === permitted ? `All ${locations.length} locations` : `${locations.length} locations`;
}

/** The export formats cap in the design (DEC-402): 50,000 rows a sheet. Mirrors src/config/reportExport.ts. */
export const CR_EXPORT_ROW_CAP = 50_000;

/** Whether the builder may offer Run. Only a person who may build can run an unsaved report. */
export function mayRun(held: (permission: string) => boolean): boolean {
  return held("report.custom.build");
}

/** Whether the Export control shows: a report that ran, and both permissions. An unsaved report needs report.custom.build
 * and report.export; a SAVED report (CRB2) needs report.custom.view and report.export, the route's own gate. */
export function mayExport(held: (permission: string) => boolean, hasRun: boolean, saved = false): boolean {
  return hasRun && held(saved ? "report.custom.view" : "report.custom.build") && held("report.export");
}

// ---------------------------------------------------------------------
// Opening a saved spec in the builder (CRB2)
// ---------------------------------------------------------------------

function isObj(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function dollarsText(cents: number): string {
  return (cents / 100).toFixed(2);
}

function filterText(field: CustomReportFieldDto, value: unknown): string | undefined {
  switch (field.type) {
    case "money":
      return typeof value === "number" && Number.isInteger(value) ? dollarsText(value) : undefined;
    case "number":
      return typeof value === "number" && Number.isInteger(value) ? String(value) : undefined;
    case "boolean":
      return typeof value === "boolean" ? String(value) : undefined;
    default:
      return typeof value === "string" ? value : undefined;
  }
}

/**
 * The builder's choices for a saved spec, so a report can be edited as a new version. Undefined when the builder cannot
 * hold the spec exactly (a test, a date range, several named locations or a row limit that only the API can write): the
 * screen then offers no Edit rather than quietly changing what the report does.
 */
export function stateFromSpec(raw: unknown, dataset: CatalogDataset, datasets: CatalogDataset[] = [dataset]): BuilderState | undefined {
  if (isObj(raw) && raw["specVersion"] === 2) return compositeFromSpec(raw, datasets);
  if (!isObj(raw) || raw["dataset"] !== dataset.id) return undefined;
  const state = freshBuilder(dataset.id);
  state.title = typeof raw["title"] === "string" ? raw["title"] : "";

  const dimensions = raw["dimensions"];
  if (!Array.isArray(dimensions)) return undefined;
  for (const name of dimensions) {
    const field = typeof name === "string" ? fieldNamed(dataset, name) : undefined;
    if (field === undefined || field.kind !== "dimension") return undefined;
    state.dimensions.push(field.name);
  }

  const measures = raw["measures"];
  if (!Array.isArray(measures)) return undefined;
  for (const item of measures) {
    if (!isObj(item) || typeof item["field"] !== "string" || typeof item["agg"] !== "string") return undefined;
    const field = fieldNamed(dataset, item["field"]);
    if (field === undefined || field.kind !== "measure" || !field.aggregations.includes(item["agg"])) return undefined;
    const measure = { field: field.name, agg: item["agg"] };
    if (item["as"] !== measureAlias(measure)) return undefined;
    state.measures.push(measure);
  }

  const filters = raw["filters"];
  if (!Array.isArray(filters)) return undefined;
  for (const item of filters) {
    if (!isObj(item) || typeof item["field"] !== "string" || typeof item["op"] !== "string") return undefined;
    const field = fieldNamed(dataset, item["field"]);
    if (field === undefined || !filterOpsFor(field).some((o) => o.op === item["op"])) return undefined;
    let value: string | undefined;
    let value2 = "";
    if (item["op"] === "between") {
      const values = item["values"];
      if (!Array.isArray(values) || values.length !== 2) return undefined;
      value = filterText(field, values[0]);
      const second = filterText(field, values[1]);
      if (second === undefined) return undefined;
      value2 = second;
    } else {
      value = filterText(field, item["value"]);
    }
    if (value === undefined) return undefined;
    state.filters.push({ id: state.nextFilterId, field: field.name, op: item["op"], value, value2 });
    state.nextFilterId += 1;
  }

  const range = raw["dateRange"];
  if (!isObj(range)) return undefined;
  if (range["kind"] === "relative") {
    if (typeof range["preset"] !== "string" || !CR_PRESETS.some((p) => p.id === range["preset"])) return undefined;
    state.dates = range["preset"];
  } else if (range["kind"] === "absolute") {
    if (typeof range["from"] !== "string" || typeof range["to"] !== "string") return undefined;
    state.dates = CR_CUSTOM_DATES;
    state.from = range["from"];
    state.to = range["to"];
  } else {
    return undefined;
  }

  const locations = raw["locations"];
  if (!isObj(locations)) return undefined;
  if (locations["kind"] === "all_permitted") {
    state.location = "all";
  } else if (locations["kind"] === "list" && Array.isArray(locations["ids"]) && locations["ids"].length === 1) {
    const id = locations["ids"][0];
    if (typeof id !== "string") return undefined;
    state.location = id;
  } else {
    return undefined;
  }

  const sort = raw["sort"];
  if (!Array.isArray(sort) || sort.length > 1) return undefined;
  const first = sort[0];
  if (first !== undefined) {
    if (!isObj(first) || typeof first["by"] !== "string" || (first["dir"] !== "asc" && first["dir"] !== "desc")) return undefined;
    if (!sortChoices(state, dataset).some((c) => c.by === first["by"])) return undefined;
    state.sortBy = first["by"];
    state.sortDir = first["dir"];
  }

  const limit = raw["limit"];
  if (limit !== null && limit !== undefined) {
    if (typeof limit !== "number" || !CR_ROW_LIMITS.some((l) => l.value === limit)) return undefined;
    state.limit = limit;
  }

  const rawOutput = raw["output"];
  if (isObj(rawOutput) && rawOutput["totals"] === false) state.totals = false;
  if (isObj(rawOutput) && rawOutput["tile"] != null) {
    const tile = rawOutput["tile"];
    const measure = state.measures[0];
    if (!isObj(tile) || Object.keys(tile).some(key => key !== "measure" && key !== "label") || measure === undefined
      || state.dimensions.length !== 0 || state.measures.length !== 1 || tile["measure"] !== measureAlias(measure)
      || typeof tile["label"] !== "string" || tile["label"].trim() === "" || tile["label"].length > 120) return undefined;
    state.tile = {label:tile["label"]};
  }
  // CRB3: a compare and a chart are held when the builder would write the very same ones.
  const compare = raw["compare"];
  if (compare !== undefined && compare !== null) {
    if (!isObj(compare) || !CR_COMPARE_CHOICES.some((c) => c.value !== "" && c.value === compare["kind"])) return undefined;
    state.compare = compare["kind"] as CompareChoice;
  }
  const output = raw["output"];
  if (isObj(output) && output["chart"] !== undefined && output["chart"] !== null) {
    const slot = chartOf(raw);
    if (slot === undefined) return undefined;
    state.chart = slot.type;
    const derived = chartSlot(state, dataset);
    if (!derived.ok || JSON.stringify(derived.chart) !== JSON.stringify(slot)) return undefined;
  }
  return state;
}

// ---------------------------------------------------------------------
// Compare (CRB3): the prior value and the change beside each measure
// ---------------------------------------------------------------------

/** "+7.7%", "-4.1%", "0.0%": a change in basis points, one decimal; N/A where there is no prior to compare with. */
export function changeText(bp: number | null | undefined): string {
  if (bp === null || bp === undefined) return NOT_AVAILABLE;
  const tenths = Math.round(Math.abs(bp) / 10);
  const text = `${Math.floor(tenths / 10)}.${tenths % 10}%`;
  return bp > 0 && tenths > 0 ? `+${text}` : bp < 0 && tenths > 0 ? `-${text}` : text;
}

export type ChangeTone = "up" | "down" | "flat" | "na";

export function changeTone(bp: number | null | undefined): ChangeTone {
  if (bp === null || bp === undefined) return "na";
  return bp > 0 ? "up" : bp < 0 ? "down" : "flat";
}

const COMPARE_WORDS: Readonly<Record<string, string>> = { prior_period: "Prior period", same_period_last_year: "Last year" };

/** The extra table columns of a compare: for each measure its prior value and its change. Empty without a compare. */
export function compareHeads(result: CustomReportResultDto): Array<{ label: string; figure: true; key: string }> {
  const compare = result.compare;
  if (compare === undefined) return [];
  const word = COMPARE_WORDS[compare.kind] ?? "Prior";
  const measures = result.columns.filter((c) => c.kind === "measure");
  return measures.flatMap((column) => [
    { key: `${column.key}_prior`, label: measures.length > 1 ? `${column.label}, ${word.toLowerCase()}` : word, figure: true as const },
    { key: `${column.key}_change`, label: measures.length > 1 ? `${column.label}, change` : "Change", figure: true as const },
  ]);
}

export interface CompareCell {
  text: string;
  tone: ChangeTone;
  /** A prior value or a change. */
  part: "prior" | "change";
}

/** The compare cells of one row (or of the totals row when `row` is "totals"), in the order of compareHeads. */
export function compareCells(result: CustomReportResultDto, row: number | "totals"): CompareCell[] {
  const compare = result.compare;
  if (compare === undefined) return [];
  const measures = result.columns.filter((c) => c.kind === "measure");
  const prior = row === "totals" ? compare.totals : compare.rows[row];
  const change = row === "totals" ? compare.totalsChangeBp : compare.changeBp[row];
  return measures.flatMap((column, at): CompareCell[] => {
    const bp = change?.[at] ?? null;
    return [
      { part: "prior", tone: "na", text: formatCell(prior?.[at] ?? null, column.type) },
      { part: "change", tone: changeTone(bp), text: changeText(bp) },
    ];
  });
}

/** "Compared with Feb 1, 2026 to Feb 14, 2026", for the line under the table's title. */
export function compareSpanText(result: CustomReportResultDto): string {
  const compare = result.compare;
  return compare === undefined ? "" : `${COMPARE_WORDS[compare.kind] ?? "Prior"}, ${dateSpanText(compare.from, compare.to)}`;
}
